import type { TalentNetworkSummaryDto } from "@artha/api-contracts";
import { normalizeCountryCode } from "@artha/utils";
import ct from "countries-and-timezones";
import type { TalentNetworkCountryAccumulator } from "./talentNetwork.types";
import {
  TALENT_NETWORK_CHART_PERCENT,
  TALENT_NETWORK_FIELD_COLORS,
} from "./talentNetwork.constants";

export function getTalentNetworkFields(data: TalentNetworkSummaryDto) {
  if (data.professionals === 0) return [];

  const categories = data.categories.filter(
    (item) => item.count > 0 && item.value !== "other",
  );
  const otherCount = Math.max(
    data.professionals -
      categories.reduce((total, item) => total + item.count, 0),
    0,
  );
  if (otherCount > 0) {
    categories.push({
      value: "other",
      label: "Other",
      count: otherCount,
      percentage: 0,
    });
  }
  const totalCount = categories.reduce((total, item) => total + item.count, 0);
  const shares = categories.map(
    (item) => (item.count / totalCount) * TALENT_NETWORK_CHART_PERCENT,
  );
  const remainingPoints =
    TALENT_NETWORK_CHART_PERCENT -
    shares.reduce((total, share) => total + Math.floor(share), 0);
  // Give rounding remainders to the largest fractional shares so the chart and legend total 100%.
  const extraPointIndexes = new Set(
    shares
      .map((share, index) => ({ index, remainder: share - Math.floor(share) }))
      .sort(
        (left, right) =>
          right.remainder - left.remainder || left.index - right.index,
      )
      .slice(0, remainingPoints)
      .map((item) => item.index),
  );
  return categories.map((item, index) => ({
    ...item,
    percentage:
      Math.floor(shares[index]) + (extraPointIndexes.has(index) ? 1 : 0),
    color:
      TALENT_NETWORK_FIELD_COLORS[item.value] ||
      TALENT_NETWORK_FIELD_COLORS.other,
  }));
}

export function getTalentNetworkCountry(
  item: TalentNetworkSummaryDto["countries"][number],
) {
  const value = item.value.trim();
  const code = normalizeCountryCode(value);
  const country =
    ct.getCountry(code) ||
    Object.values(ct.getAllCountries()).find(
      (candidate) =>
        candidate.name.toLocaleLowerCase() === value.toLocaleLowerCase(),
    );
  return {
    label: country?.name || item.label,
    code: country?.id.toLowerCase() || null,
  };
}

export function getTalentNetworkCountries(data: TalentNetworkSummaryDto) {
  const countries = new Map<string, TalentNetworkCountryAccumulator>();

  for (const item of data.countries) {
    const country = getTalentNetworkCountry(item);
    if (!country.code || item.count <= 0) continue;
    const existing = countries.get(country.code);
    countries.set(country.code, {
      code: country.code,
      label: country.label,
      count: (existing?.count || 0) + item.count,
      fallbackPercentage:
        (existing?.fallbackPercentage || 0) + item.percentage,
    });
  }

  return [...countries.values()]
    .map(({ fallbackPercentage, ...country }) => ({
      ...country,
      percentage:
        data.professionals > 0
          ? Math.round((country.count / data.professionals) * 100)
          : fallbackPercentage,
    }))
    .filter((country) => country.percentage > 0);
}
